import React, { useEffect, useMemo, useRef } from "react";

/**
 * ParticleSphere
 * An animated rotating 3D particle sphere rendered in SVG with
 * requestAnimationFrame. Uses Fibonacci lattice distribution so particles are
 * evenly spaced over the surface. Each frame rotates the sphere around the
 * Y axis and adjusts particle size & opacity based on depth, producing a
 * convincing 3D feel at ~60fps with zero external dependencies.
 *
 * Props:
 *  - size:      rendered size in px (default 420)
 *  - count:     particle count (default 140)
 *  - color:     base particle colour (default brand-700)
 *  - speed:     rotation speed (default 0.0035 rad/frame)
 *  - haloColor: central halo gradient colour
 */
export default function ParticleSphere({
  size = 420,
  count = 140,
  color,
  accentColor,
  speed = 0.0035
}) {
  const svgRef = useRef(null);
  const angleRef = useRef(0);

  // Resolve palette from CSS variables so sphere follows active theme.
  const resolvedColor = color || "hsl(var(--brand-300))";
  const resolvedAccent = accentColor || "hsl(var(--accentc-300))";

  // Fibonacci sphere point distribution — computed once.
  const points = useMemo(() => {
    const pts = [];
    const phi = Math.PI * (Math.sqrt(5) - 1);
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = phi * i;
      pts.push({
        x: Math.cos(theta) * r,
        y,
        z: Math.sin(theta) * r,
        // Random seed for subtle per-particle pulse
        seed: Math.random()
      });
    }
    return pts;
  }, [count]);

  // A few "accent nodes" that are larger & pulse — chosen evenly.
  const accentIdx = useMemo(
    () => new Set([10, 34, 58, 82, 100, 120].filter((i) => i < count)),
    [count]
  );

  useEffect(() => {
    let rafId;
    let lastT = performance.now();
    const radius = size * 0.42;
    const cx = size / 2;
    const cy = size / 2;

    const tick = (t) => {
      const dt = Math.min(50, t - lastT);
      lastT = t;
      angleRef.current += speed * (dt / 16.67);
      const angle = angleRef.current;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const tiltCos = Math.cos(0.35);
      const tiltSin = Math.sin(0.35);

      const svg = svgRef.current;
      if (svg) {
        const circles = svg.querySelectorAll("circle.pt");
        for (let i = 0; i < points.length; i++) {
          const p = points[i];
          // Rotate around Y-axis
          let x = p.x * cosA + p.z * sinA;
          let y = p.y;
          let z = -p.x * sinA + p.z * cosA;
          // Tilt the sphere slightly forward (rotate around X-axis)
          const y2 = y * tiltCos - z * tiltSin;
          const z2 = y * tiltSin + z * tiltCos;
          y = y2;
          z = z2;

          const depth = (z + 1) / 2; // 0 (back) → 1 (front)
          const px = cx + x * radius;
          const py = cy - y * radius; // SVG y-axis is inverted
          const isAccent = accentIdx.has(i);
          const pulse =
            0.85 + 0.15 * Math.sin(t * 0.003 + p.seed * 6.28);
          const sizeR = (isAccent ? 5.5 : 3.2) + depth * (isAccent ? 3.5 : 2.8);
          const op = 0.45 + depth * 0.55;

          const c = circles[i];
          if (c) {
            c.setAttribute("cx", px.toFixed(2));
            c.setAttribute("cy", py.toFixed(2));
            c.setAttribute("r", (sizeR * (isAccent ? pulse : 1)).toFixed(2));
            c.setAttribute("opacity", op.toFixed(3));
            if (isAccent) c.setAttribute("fill", resolvedAccent);
          }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [points, accentIdx, size, speed, resolvedAccent]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      height="100%"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="psphere-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={resolvedAccent} stopOpacity="0.35" />
          <stop offset="40%" stopColor={resolvedColor} stopOpacity="0.12" />
          <stop offset="100%" stopColor={resolvedColor} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="psphere-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={resolvedAccent} stopOpacity="0.55" />
          <stop offset="70%" stopColor={resolvedAccent} stopOpacity="0.05" />
          <stop offset="100%" stopColor={resolvedAccent} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Halo */}
      <circle cx={size / 2} cy={size / 2} r={size * 0.48} fill="url(#psphere-halo)" />

      {/* Orbit rings */}
      <g transform={`translate(${size / 2} ${size / 2})`} opacity="0.35">
        <ellipse
          cx="0" cy="0"
          rx={size * 0.42} ry={size * 0.16}
          fill="none" stroke={resolvedColor} strokeWidth="0.8"
          strokeDasharray="2 6"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0" to="360"
            dur="28s"
            repeatCount="indefinite"
          />
        </ellipse>
        <ellipse
          cx="0" cy="0"
          rx={size * 0.42} ry={size * 0.1}
          fill="none" stroke={resolvedAccent} strokeWidth="0.8"
          strokeDasharray="1 10"
          transform="rotate(-22)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0" to="-360"
            dur="40s"
            repeatCount="indefinite"
          />
        </ellipse>
      </g>

      {/* Core glow */}
      <circle cx={size / 2} cy={size / 2} r={size * 0.18} fill="url(#psphere-core)">
        <animate
          attributeName="r"
          values={`${size * 0.16};${size * 0.2};${size * 0.16}`}
          dur="4.5s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Particles */}
      <g>
        {points.map((p, i) => {
          // Compute initial position (no rotation yet) so particles are
          // visible even before the first animation frame runs.
          const tiltCos = Math.cos(0.35);
          const tiltSin = Math.sin(0.35);
          const y0 = p.y * tiltCos - p.z * tiltSin;
          const z0 = p.y * tiltSin + p.z * tiltCos;
          const depth = (z0 + 1) / 2;
          const isAccent = accentIdx.has(i);
          const r0 = (isAccent ? 5.5 : 3.2) + depth * (isAccent ? 3.5 : 2.8);
          const op0 = 0.45 + depth * 0.55;
          return (
            <circle
              key={i}
              className="pt"
              cx={(size / 2 + p.x * size * 0.42).toFixed(2)}
              cy={(size / 2 - y0 * size * 0.42).toFixed(2)}
              r={r0.toFixed(2)}
              opacity={op0.toFixed(3)}
              fill={isAccent ? resolvedAccent : resolvedColor}
            />
          );
        })}
      </g>
    </svg>
  );
}
